import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";

/**
 * Guest access links — mint, track, and revoke private demo invitations.
 *
 * A link is one email address plus a fixed allowance. The recipient signs in
 * with a code we email them, runs each unlocked agent once, and keeps read
 * access to what they produced. See backend/config/guestAccess.js.
 */
export default function AccessLinksTab({ currentUser }) {
  const [links, setLinks] = useState([]);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [flash, setFlash] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [listRes, optRes] = await Promise.all([
        api.adminGetAccessLinks(),
        api.adminGetAccessLinkOptions(),
      ]);
      setLinks(listRes.data.links || []);
      setOptions(optRes.data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not load access links.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Flash messages are informational ("copied", "re-sent"); clear them so the
  // panel does not accumulate stale confirmations.
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(""), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setFlash("Link copied to clipboard.");
    } catch {
      // Clipboard is blocked outside a secure context — show it so the link
      // can still be copied by hand rather than silently doing nothing.
      window.prompt("Copy this link:", url);
    }
  };

  const resend = async (link) => {
    setBusyId(link.id);
    try {
      const res = await api.adminResendAccessLink(link.id);
      setFlash(res.data.message || "Invitation re-sent.");
    } catch (err) {
      setError(
        err.response?.data?.error || "Could not re-send the invitation.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const extend = async (link, days, notify) => {
    setBusyId(link.id);
    setError("");
    try {
      const res = await api.adminExtendAccessLink(link.id, { days, notify });
      // An extension whose email bounced is still an extension — report the
      // send failure rather than implying the guest was told.
      setFlash(res.data.emailError || res.data.message || "Access extended.");
      await load();
      return true;
    } catch (err) {
      setError(err.response?.data?.error || "Could not extend access.");
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (link) => {
    if (
      !window.confirm(
        `Revoke access for ${link.email}?\n\nThey will be signed out immediately and the link will stop working. This cannot be undone.`,
      )
    ) {
      return;
    }
    setBusyId(link.id);
    try {
      await api.adminRevokeAccessLink(link.id);
      setFlash(`Access revoked for ${link.email}.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not revoke the link.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap",
          marginBottom: "6px",
        }}
      >
        <div>
          <h2
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: "var(--text-primary)",
              margin: 0,
            }}
          >
            Access Links
          </h2>
          <p
            style={{
              fontSize: "12.5px",
              color: "var(--text-muted)",
              margin: "4px 0 0",
            }}
          >
            Private, passwordless invitations. The recipient signs in with a
            code emailed to them, and gets a fixed number of analyses.
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm((v) => !v)}
          style={{ whiteSpace: "nowrap" }}
        >
          {showForm ? "Cancel" : "+ New link"}
        </button>
      </div>

      {flash && (
        <div
          style={{
            margin: "14px 0 0",
            padding: "9px 12px",
            borderRadius: "8px",
            fontSize: "12.5px",
            background: "rgba(6,182,212,0.1)",
            border: "1px solid rgba(6,182,212,0.3)",
            color: "var(--teal-bright)",
          }}
        >
          {flash}
        </div>
      )}
      {error && (
        <div className="alert alert-error" style={{ margin: "14px 0 0" }}>
          {error}
        </div>
      )}

      {showForm && options && (
        <CreateLinkForm
          options={options}
          onCancel={() => setShowForm(false)}
          onCreated={async (msg) => {
            setShowForm(false);
            setFlash(msg);
            await load();
          }}
          onError={setError}
        />
      )}

      <div style={{ marginTop: "18px" }}>
        {loading ? (
          <div className="spinner-center" style={{ padding: "40px 0" }}>
            <div className="spinner-lg" />
          </div>
        ) : links.length === 0 ? (
          <EmptyState onCreate={() => setShowForm(true)} />
        ) : (
          <div style={{ display: "grid", gap: "10px" }}>
            {links.map((link) => (
              <LinkRow
                key={link.id}
                link={link}
                busy={busyId === link.id}
                showOwner={currentUser?.role === "admin"}
                onCopy={() => copy(link.url)}
                onResend={() => resend(link)}
                onExtend={(days, notify) => extend(link, days, notify)}
                onRevoke={() => revoke(link)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Create ──────────────────────────────────────────────────────────────────

function CreateLinkForm({ options, onCancel, onCreated, onError }) {
  const allIds = useMemo(
    () => options.agents.filter((a) => a.available).map((a) => a.id),
    [options],
  );
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [ttlDays, setTtlDays] = useState(options.defaults.ttlDays);
  const [picked, setPicked] = useState(allIds);
  const [saving, setSaving] = useState(false);

  const toggle = (id) =>
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || picked.length === 0 || saving) return;
    setSaving(true);
    onError("");
    try {
      const res = await api.adminCreateAccessLink({
        email: email.trim(),
        label: label.trim(),
        ttlDays,
        allowedAgents: picked,
      });
      // A created link whose email bounced is still a usable link — say so
      // rather than reporting plain success and letting them assume delivery.
      onCreated(
        res.data.emailed
          ? `Access link created and emailed to ${email.trim()}.`
          : res.data.emailError ||
              `Access link created for ${email.trim()}. Copy it below and send it yourself.`,
      );
    } catch (err) {
      onError(err.response?.data?.error || "Could not create the link.");
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      style={{
        marginTop: "16px",
        padding: "18px",
        borderRadius: "12px",
        border: "1px solid var(--border)",
        background: "rgba(255,255,255,0.03)",
      }}
    >
      <div
        style={{
          display: "grid",
          gap: "14px",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
        }}
      >
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Email address *</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@company.com"
          />
          <p
            style={{
              fontSize: "11px",
              color: "var(--text-muted)",
              margin: "6px 0 0",
            }}
          >
            Sign-in codes go here. Only this inbox can use the link.
          </p>
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Label (optional)</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Acme Corp — prospect"
          />
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Access window</label>
          <select
            value={ttlDays}
            onChange={(e) => setTtlDays(Number(e.target.value))}
          >
            {[7, 14, 30]
              .filter((d) => d <= options.defaults.maxTtlDays)
              .map((d) => (
                <option key={d} value={d}>
                  {d} days
                </option>
              ))}
          </select>
          <p
            style={{
              fontSize: "11px",
              color: "var(--text-muted)",
              margin: "6px 0 0",
            }}
          >
            Starts now, not when they first open it.
          </p>
        </div>
      </div>

      <div style={{ marginTop: "18px" }}>
        <label
          style={{
            display: "block",
            fontSize: "12px",
            fontWeight: 600,
            color: "var(--text-secondary)",
            marginBottom: "8px",
          }}
        >
          Analyses they can run — once each
        </label>
        <div style={{ display: "grid", gap: "7px" }}>
          {options.agents.map((agent) => (
            <label
              key={agent.id}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "9px",
                padding: "9px 11px",
                borderRadius: "9px",
                border: "1px solid var(--border)",
                background: picked.includes(agent.id)
                  ? "rgba(6,182,212,0.08)"
                  : "transparent",
                cursor: agent.available ? "pointer" : "not-allowed",
                opacity: agent.available ? 1 : 0.45,
              }}
            >
              <input
                type="checkbox"
                disabled={!agent.available}
                checked={picked.includes(agent.id)}
                onChange={() => toggle(agent.id)}
                style={{ marginTop: "2px", width: "auto" }}
              />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "var(--text-primary)",
                  }}
                >
                  {agent.icon ? `${agent.icon} ` : ""}
                  {agent.name}
                  {!agent.available && " — unavailable"}
                </span>
                {agent.description && (
                  <span
                    style={{
                      display: "block",
                      fontSize: "11.5px",
                      color: "var(--text-muted)",
                      marginTop: "2px",
                      lineHeight: 1.5,
                    }}
                  >
                    {agent.description.slice(0, 130)}
                    {agent.description.length > 130 ? "…" : ""}
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: "9px", marginTop: "18px" }}>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={saving || !email.trim() || picked.length === 0}
        >
          {saving ? "Creating…" : "Create link"}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── List ────────────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  pending: {
    label: "Not used yet",
    color: "#a78bfa",
    bg: "rgba(167,139,250,0.12)",
    border: "rgba(167,139,250,0.3)",
  },
  active: {
    label: "Active",
    color: "#22d3ee",
    bg: "rgba(6,182,212,0.12)",
    border: "rgba(6,182,212,0.3)",
  },
  expired: {
    label: "Expired",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.12)",
    border: "rgba(245,158,11,0.3)",
  },
  revoked: {
    label: "Revoked",
    color: "#f87171",
    bg: "rgba(248,113,113,0.12)",
    border: "rgba(248,113,113,0.3)",
  },
};

function LinkRow({
  link,
  busy,
  showOwner,
  onCopy,
  onResend,
  onExtend,
  onRevoke,
}) {
  const status = STATUS_STYLE[link.status] || STATUS_STYLE.pending;
  const dead = link.status === "revoked";
  const [extending, setExtending] = useState(false);

  return (
    <div
      style={{
        padding: "14px 16px",
        borderRadius: "11px",
        border: "1px solid var(--border)",
        background: "rgba(255,255,255,0.03)",
        opacity: dead ? 0.6 : 1,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
          marginBottom: "9px",
        }}
      >
        <span
          style={{
            fontSize: "13.5px",
            fontWeight: 600,
            color: "var(--text-primary)",
          }}
        >
          {link.email}
        </span>
        <span
          style={{
            fontSize: "9.5px",
            fontWeight: 800,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            padding: "2px 8px",
            borderRadius: "5px",
            color: status.color,
            background: status.bg,
            border: `1px solid ${status.border}`,
          }}
        >
          {status.label}
        </span>
        {link.label && (
          <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
            {link.label}
          </span>
        )}
      </div>

      <div
        style={{
          display: "flex",
          gap: "18px",
          flexWrap: "wrap",
          fontSize: "11.5px",
          color: "var(--text-muted)",
          marginBottom: "11px",
        }}
      >
        <Fact
          label="Analyses used"
          value={`${link.runsUsed} of ${link.runsTotal}`}
        />
        <Fact
          label="Access ends"
          value={link.expiresAt ? formatDate(link.expiresAt) : "—"}
        />
        <Fact
          label="Last sign-in"
          value={link.lastLoginAt ? formatDate(link.lastLoginAt) : "—"}
        />
        {link.extensionCount > 0 && (
          <Fact
            label="Extended"
            value={`${link.extensionCount}× · last ${formatDate(link.lastExtendedAt)}`}
          />
        )}
        {showOwner && (
          <Fact
            label="Created by"
            value={link.createdBy?.fullName || link.createdBy?.username || "—"}
          />
        )}
      </div>

      <div style={{ display: "flex", gap: "7px", flexWrap: "wrap" }}>
        <SmallButton onClick={onCopy} disabled={dead}>
          Copy link
        </SmallButton>
        <SmallButton onClick={onResend} disabled={dead || busy}>
          {busy ? "Working…" : "Email Invite Link"}
        </SmallButton>
        {!dead && (
          <SmallButton onClick={() => setExtending((v) => !v)} disabled={busy}>
            {extending ? "Cancel" : "Extend access"}
          </SmallButton>
        )}
        {!dead && (
          <SmallButton onClick={onRevoke} disabled={busy} danger>
            Revoke
          </SmallButton>
        )}
      </div>

      {extending && !dead && (
        <ExtendForm
          link={link}
          busy={busy}
          onSubmit={async (days, notify) => {
            const ok = await onExtend(days, notify);
            if (ok) setExtending(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Inline "give them longer" control.
 *
 * Every link has a real expiry from the moment it is created, so extending
 * always pushes a date. The only distinction left is whether that date is
 * still ahead (added to the end) or behind (restarted from today).
 */
function ExtendForm({ link, busy, onSubmit }) {
  const [days, setDays] = useState(7);
  const [notify, setNotify] = useState(true);
  const expired = link.status === "expired";

  return (
    <div
      style={{
        marginTop: "11px",
        paddingTop: "11px",
        borderTop: "1px solid var(--border)",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexWrap: "wrap",
      }}
    >
      <select
        value={days}
        onChange={(e) => setDays(Number(e.target.value))}
        style={{
          width: "auto",
          padding: "5px 9px",
          fontSize: "12px",
          borderRadius: "7px",
          border: "1px solid var(--border)",
          background: "var(--bg-input)",
          color: "var(--text-primary)",
        }}
      >
        {[7, 14, 30].map((d) => (
          <option key={d} value={d}>
            + {d} days
          </option>
        ))}
      </select>

      <span style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>
        {expired
          ? `Expired — the ${days} days will run from today.`
          : `Added to their current end date.`}
      </span>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "11.5px",
          color: "var(--text-secondary)",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={notify}
          onChange={(e) => setNotify(e.target.checked)}
          style={{ width: "auto" }}
        />
        Email them
      </label>

      <SmallButton onClick={() => onSubmit(days, notify)} disabled={busy}>
        {busy ? "Extending…" : "Confirm"}
      </SmallButton>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <span>
      {label}:{" "}
      <strong style={{ color: "var(--text-secondary)", fontWeight: 600 }}>
        {value}
      </strong>
    </span>
  );
}

function SmallButton({ children, onClick, disabled, danger }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontSize: "11.5px",
        fontWeight: 600,
        padding: "5px 11px",
        borderRadius: "7px",
        border: `1px solid ${danger ? "rgba(248,113,113,0.3)" : "var(--border)"}`,
        background: danger
          ? "rgba(248,113,113,0.08)"
          : "rgba(255,255,255,0.04)",
        color: danger ? "#f87171" : "var(--text-secondary)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "50px 20px",
        border: "1px dashed var(--border)",
        borderRadius: "12px",
      }}
    >
      <div style={{ fontSize: "30px", opacity: 0.4, marginBottom: "10px" }}>
        🔗
      </div>
      <p
        style={{
          fontSize: "13.5px",
          color: "var(--text-secondary)",
          margin: "0 0 4px",
        }}
      >
        No access links yet
      </p>
      <p
        style={{
          fontSize: "12px",
          color: "var(--text-muted)",
          margin: "0 0 18px",
        }}
      >
        Create one to give a prospect a private, time-limited look at the
        platform.
      </p>
      <button className="btn btn-primary" onClick={onCreate}>
        + New link
      </button>
    </div>
  );
}

function formatDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}
